'use client';
import {
  Breakpoints,
  Button,
  ButtonColor,
  ButtonSize,
  Carousel,
  DateFormat,
  Faq,
  formatDate,
  formatName,
  formatPrice,
  IconCheck,
  IconLinkExternal,
  IconVideo,
  isIconName,
  pluralize,
  ProfileImage,
  Rating,
  RatingTextColor,
  RenderIcon,
  useMediaQuery,
} from '@leland-dev/leland-ui-library';
import Link from 'next/link';
import React, { type FC, useRef } from 'react';

import { getCoachUrl } from '../../utils/coach';
import useVisibility from '../../utils/hooks/useVisibility';
import SvgIcon, { Svg } from '../SvgIcon';
import Breadcrumbs from '../breadcrumbs/Breadcrumbs';
import { useCategoryBreadcrumbs } from '../breadcrumbs/useCategoryBreadcrumbs';
import { VerticalCoachCard } from '../category/VerticalCoachCard';
import { GroupClassImage } from '../classes/GroupClassImage';
import { Bio } from '../common/Bio';

import { BootcampCoachCard } from './BootcampCoachCard';
import { BootcampCohortList } from './BootcampCohortList';
import {
  BootcampCohortSelector,
  BootcampCohortSelectorProvider,
  useBootcampCohortSelector,
} from './BootcampCohortSelector';
import { BootcampEnrollCard } from './BootcampEnrollCard';
import { BootcampGetNotifiedInput } from './BootcampGetNotifiedInput';
import { BootcampReview } from './BootcampReview';
import { BootcampVideo } from './BootcampVideo';
import { useSelectCohortModal } from './SelectCohortModal';
import {
  type BootcampPageBootcampCurriculumFragment,
  type BootcampPageBootcampFragment,
  type BootcampPageCategoryFragment,
  type BootcampPageReviewFragment,
} from './__generated-gql-types__/BootcampPage.generated';
import {
  formatBootcampCohortDate,
  openBootcampCohortCheckoutPage,
} from './utils';

const BOOTCAMPS_RELATIVE_URL = '/bootcamps';

export interface BootcampPageProps {
  bootcamp: BootcampPageBootcampFragment;
  category: BootcampPageCategoryFragment;
}

const BootcampPage: React.FC<BootcampPageProps> = ({ bootcamp, category }) => {
  const isMd = useMediaQuery(Breakpoints.MD);

  const selectCohortRef = useRef<HTMLDivElement>(null);
  const isSelectCohortButtonVisible = !useVisibility(selectCohortRef, false);

  const breadcrumbs = useCategoryBreadcrumbs({
    category,
    productOffering: [{ label: 'Bootcamps', url: BOOTCAMPS_RELATIVE_URL }],
  });

  const [selectedCohort] = useBootcampCohortSelector();

  const { setOpen: setIsSelectCohortModalOpen, Modal: SelectCohortModal } =
    useSelectCohortModal();

  const headerText = category?.name
    ? `${category.name} Bootcamps`
    : 'Application Bootcamps';

  const allCoaches = bootcamp.coaches ?? [];
  const cohortCoaches = selectedCohort?.coaches ?? [];

  const reviewExists = (review: BootcampPageReviewFragment) =>
    review?.comment != null && review?.comment !== '';
  const hasReviews =
    !!bootcamp.reviews?.length && bootcamp.reviews.some(reviewExists);

  return (
    <div>
      <div className="flex-col text-left md:m-auto md:flex md:pt-8">
        <div className="mx-auto gap-20 md:flex md:px-10">
          <div className="3xl:max-w-7xl mx-auto flex flex-1 flex-col lg:max-w-4xl">
            <GroupClassImage
              containerClassName="w-full"
              className="object-cover md:hidden md:rounded-t-md"
              name={bootcamp.title}
              imageLink={bootcamp.imageLink}
              sizes={{ dflt: '100%' }}
            />
            <div className="flex items-center justify-between p-5 md:p-0">
              <Breadcrumbs breadcrumbs={breadcrumbs} />
            </div>
            <div className="mb-8 flex flex-col gap-5 px-5 md:mt-6 md:px-0">
              <h1 className="text-3xl font-medium md:text-4xl">
                {bootcamp.title || headerText}
              </h1>
              <div className="flex flex-wrap items-center gap-1 text-sm font-medium tracking-wider text-leland-gray-light">
                {bootcamp.type} ·{' '}
                {pluralize(
                  bootcamp.curriculums.length,
                  'LIVE SESSION',
                ).toUpperCase()}
                {' · '}
                <div className="flex items-center gap-1">
                  <IconVideo className="size-4" /> ALL SESSIONS RECORDED
                </div>
              </div>
              {bootcamp.reviewCount && bootcamp.reviewRate ? (
                <div className="hidden items-center gap-2 font-medium md:flex">
                  <Rating
                    rate={bootcamp.reviewRate}
                    showRate
                    textColor={RatingTextColor.DARK}
                  />
                  <span className="text-lg text-leland-gray-extra-light">
                    {bootcamp.reviewCount} reviews
                  </span>
                </div>
              ) : null}
              <div className="text-xl text-leland-gray-light md:text-2xl">
                {bootcamp.headline}
              </div>
              <div className="xl:hidden" ref={selectCohortRef}>
                <div className="mb-5 flex items-center justify-between md:hidden">
                  <div className="flex gap-1">
                    <div className="text-2xl font-medium">
                      {formatPrice(bootcamp.price)}
                    </div>
                    {bootcamp.originalPrice ? (
                      <span className="text-2xl text-leland-gray-extra-light line-through">
                        {formatPrice(bootcamp.originalPrice)}
                      </span>
                    ) : null}
                  </div>
                  {bootcamp.reviewCount && bootcamp.reviewRate ? (
                    <div className="flex items-center gap-2 font-medium">
                      <Rating
                        rate={bootcamp.reviewRate}
                        totalStars={1}
                        textColor={RatingTextColor.DARK}
                        showRate
                      />
                      <span className="text-lg text-leland-gray-extra-light">
                        {bootcamp.reviewCount} reviews
                      </span>
                    </div>
                  ) : null}
                </div>
                <Button
                  label="Select a cohort"
                  buttonColor={ButtonColor.PRIMARY}
                  onClick={() => setIsSelectCohortModalOpen(true)}
                />
              </div>

              <div className="space-y-10 divide-y">
                {allCoaches.length ? (
                  <div>
                    <h2 className="text-sm font-medium tracking-wider text-leland-gray-light">
                      LEARN FROM INDUSTRY EXPERTS
                    </h2>
                    <div className="container mt-4">
                      <Carousel
                        key="coaches"
                        swiperProps={{
                          slidesPerGroupAuto: false,
                          spaceBetween: 20,
                          slidesPerView: isMd ? 2.25 : 1.15,
                        }}
                        disableArrowGradient
                        buttonSize={ButtonSize.MEDIUM}
                        items={allCoaches.map((coach) => (
                          <BootcampCoachCard
                            key={coach.id}
                            coach={coach}
                            categoryUrn={category.id}
                          />
                        ))}
                      />
                    </div>
                    <div className="mt-5 md:hidden">
                      <BootcampGetNotifiedInput bootcampUrn={bootcamp.id} />
                    </div>
                    <div className="mt-8 flex flex-col gap-8 md:hidden">
                      {bootcamp.scholarshipUrl ? (
                        <div className="text-center">
                          <Link
                            id="bootcamp_page--apply_for_scholarship_link_mobile"
                            href={bootcamp.scholarshipUrl}
                            className="text-lg font-medium text-leland-gray-dark underline"
                          >
                            Apply for a Scholarship
                          </Link>
                        </div>
                      ) : null}
                    </div>
                  </div>
                ) : null}
                <Bio
                  className="pt-10 text-xl"
                  bio={bootcamp.description}
                  maxLines={8}
                  readMoreButtonClassName="text-lg text-leland-gray-dark underline mt-4"
                />

                {bootcamp.targetAudiences ? (
                  <div className="pt-10">
                    <h2 className="mb-6 text-2xl font-medium md:text-3xl">
                      {bootcamp.htmlHeading.targetAudiences}
                    </h2>
                    <ul>
                      {bootcamp.targetAudiences.map((audience, index) => (
                        <li key={index} className="flex gap-4 py-6">
                          <IconCheck className="size-9 rounded-full bg-leland-primary-extra-light p-1.5 text-leland-dark-green" />
                          <div className="flex-1 text-xl">
                            <span className="font-medium">
                              {audience.title}.
                            </span>{' '}
                            <span className="text-leland-gray-light">
                              {audience.description}
                            </span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
                {bootcamp.htmlHeading.offerings ? (
                  <div className="pt-10 text-lg">
                    <h2 className="mb-6 text-2xl font-medium md:text-3xl">
                      {bootcamp.htmlHeading.offerings}
                    </h2>
                    <div className="flex flex-col gap-3">
                      {/* Container for horizontal scrolling on mobile */}
                      <div className="flex gap-3 overflow-x-auto pb-4 sm:grid sm:grid-cols-2 sm:overflow-visible lg:grid-cols-3">
                        {bootcamp.offerings.map((offering, index) => (
                          <div
                            key={index}
                            className="w-10/12 flex-none rounded border border-leland-gray-stroke p-5 md:w-auto" // w-10/12 to make cards take up most of the screen width on mobile
                          >
                            {offering.icon && isIconName(offering.icon) ? (
                              <RenderIcon
                                icon={offering.icon}
                                className="mb-3 size-6"
                              />
                            ) : null}
                            <div className="flex flex-col gap-1">
                              <div className="font-medium">
                                {offering.title}
                              </div>
                              <div className="text-leland-gray-light">
                                {offering.description}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
          <div className="mb-8 hidden md:max-w-xl xl:block">
            <BootcampEnrollCard bootcamp={bootcamp} />
          </div>
        </div>
        <BootcampPageCurriculumSection bootcamp={bootcamp} />
        <div id="bootcamp_page--coaches" className="px-5 md:px-10 xl:px-0">
          <div className="mx-auto my-10 w-full md:mt-24 md:max-w-7xl">
            <div className="flex justify-between">
              <div className="flex flex-col">
                <h2 className="text-2xl font-medium text-leland-gray-dark md:text-3xl">
                  {bootcamp.htmlHeading.coaches}
                </h2>
                <div className="mt-3 text-lg text-leland-gray-light md:text-xl">
                  Leland bootcamps are hosted by our top coaches
                </div>
              </div>
              {bootcamp.upcomingCohorts.length > 0 ? (
                <div className="hidden md:block">
                  <BootcampCohortSelector />
                </div>
              ) : null}
            </div>
            <div className="mb-20 mt-10">
              {cohortCoaches.length > 1 ? (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
                  {cohortCoaches.map((coach) => (
                    <VerticalCoachCard
                      key={coach.id}
                      coach={{ ...coach, availability: undefined }} //
                      categoryUrn={category?.id}
                      hidePrice
                      hideSchedulingCTA
                    />
                  ))}
                </div>
              ) : cohortCoaches.length === 1 ? (
                <div className="flex gap-8">
                  <ProfileImage
                    className="size-16 shrink-0"
                    pictureLink={cohortCoaches[0].user.pictureLink}
                    firstName={cohortCoaches[0].user.firstName}
                    lastName={cohortCoaches[0].user.lastName}
                  />

                  <div className="flex flex-1 flex-col gap-4">
                    <div>
                      <div className="flex flex-row justify-between">
                        <div className="flex flex-col md:flex-row md:items-center md:gap-2">
                          <div className="text-2xl font-medium">
                            {formatName(cohortCoaches[0].user)}
                          </div>
                          <Link
                            id="bootcamp_page--coach_profile"
                            className="flex items-center gap-1 text-xl font-medium
                              text-leland-primary underline"
                            href={getCoachUrl({
                              coachSlug: cohortCoaches[0].slug,
                              categorySlug: category?.slug,
                            })}
                            target="_blank"
                          >
                            View Profile <IconLinkExternal className="size-4" />
                          </Link>
                        </div>
                        {cohortCoaches[0].averageReviewRate ? (
                          <div className="flex flex-col md:flex-row md:items-center md:gap-1">
                            <Rating
                              rate={cohortCoaches[0].averageReviewRate}
                              showRate
                              totalStars={1}
                              textColor={RatingTextColor.DARK}
                            />

                            <div className="ml-1 text-lg text-leland-gray-extra-light">
                              {cohortCoaches[0].reviewTotal} Reviews
                            </div>
                          </div>
                        ) : null}
                      </div>
                      <div className="text-xl">{cohortCoaches[0].headline}</div>
                    </div>

                    <Bio
                      className="text-xl text-leland-gray-light"
                      bio={cohortCoaches[0].bio ?? ''}
                      readMoreButtonClassName="text-lg text-leland-gray-dark underline mt-4"
                    />
                  </div>
                </div>
              ) : (
                <div className="bg-leland-gray-hover p-4">
                  <div className="text-xl font-medium text-leland-gray-light">
                    Coaches for this cohort are being finalized!
                  </div>
                  <div className="text-lg text-leland-gray-light">
                    We&apos;re hand-selecting the experts who will lead this
                    experience. Check back soon to see who&apos;s leading this
                    cohort.
                  </div>
                </div>
              )}
            </div>
            {bootcamp.scholarshipUrl ? (
              <div
                className={`relative flex-col justify-between gap-6 rounded-2xl bg-leland-blue-light p-6 md:flex-row md:gap-0 md:p-16 ${
                  hasReviews ? '-mb-32 md:-mb-28' : ''
                } flex`}
              >
                <div className="flex flex-1 flex-col gap-5">
                  <div className="flex flex-col gap-1">
                    <div className="text-sm font-medium uppercase tracking-wider text-leland-blue">
                      bootcamp scholarships
                    </div>
                    <div className="text-2xl font-medium md:text-3xl">
                      Don&apos;t let money be the dealbreaker.
                    </div>
                  </div>
                  <div className="text-lg md:text-xl">
                    At Leland, our vision is to make expertise accessible, and
                    our bootcamp financial aid program is one way we&apos;re
                    working to achieve that goal. We don&apos;t want your
                    financial situation to keep you from the expert training and
                    advice you need to reach your goal.
                  </div>
                  <div className="w-fit">
                    <Button
                      id="bootcamp_page--apply_for_scholarship_button"
                      buttonColor={ButtonColor.BLACK}
                      label="Apply for a scholarship"
                      onClick={() =>
                        window.open(bootcamp.scholarshipUrl ?? '', '_blank')
                      }
                    />
                  </div>
                </div>
                <div className="order-first flex-1 md:order-last">
                  <SvgIcon svgType={Svg.GIFT} className="h-40 w-full md:h-64" />
                </div>
              </div>
            ) : null}
          </div>
        </div>
        {hasReviews || bootcamp.videoLink ? (
          <div className="bg-leland-black">
            <div
              className={`mx-auto mb-10 ${
                bootcamp.scholarshipUrl ? 'mt-32' : 'mt-10'
              } w-full p-5 md:max-w-7xl md:px-10 xl:px-0`}
            >
              <h2
                className={`mb-10 text-2xl md:text-4xl ${!hasReviews ? 'text-center' : ''} font-medium text-leland-white`}
              >
                {bootcamp.htmlHeading.reviews}
              </h2>
              <div
                className={`flex flex-col gap-3 p-0 md:flex-row ${!hasReviews ? 'justify-center' : ''}`}
              >
                {bootcamp.reviews?.[0] && reviewExists(bootcamp.reviews[0]) ? (
                  <BootcampReview review={bootcamp.reviews[0]} />
                ) : null}
                {bootcamp.videoLink ? (
                  <BootcampVideo
                    className={`min-h-64 flex-1 md:min-h-0 ${!hasReviews ? 'h-64' : ''}`}
                    youtubeId={bootcamp.videoLink.youtubeId}
                    thumbnailLink={bootcamp.videoLink.thumbnailLink}
                  />
                ) : null}
                {bootcamp.reviews?.[1] && reviewExists(bootcamp.reviews[1]) ? (
                  <BootcampReview review={bootcamp.reviews[1]} />
                ) : null}
              </div>
            </div>
          </div>
        ) : null}
        <div className="mx-auto my-16 w-full px-5 md:max-w-7xl md:px-10 xl:px-0">
          <Faq
            title="Frequently asked questions"
            items={bootcamp.faqs.map((item) => ({
              question: item.title,
              answer: item.description,
            }))}
          />
        </div>
        {bootcamp.upcomingCohorts.length > 0 ? (
          <div className="bg-leland-dark-green text-leland-white">
            <div className="mx-auto flex w-full flex-col gap-20 px-5 py-20 md:max-w-7xl md:flex-row md:px-10 md:py-24 xl:px-0">
              <div className="m-0 w-full flex-1">
                <div className="mb-6 flex flex-col gap-3">
                  <span className="text-sm font-medium tracking-wider text-leland-yellow">
                    SELECT AN UPCOMING COHORT
                  </span>
                  <h2 className="text-3xl font-medium md:text-4xl">
                    {bootcamp.title}
                  </h2>
                </div>

                <div className="text-xl">{bootcamp.description}</div>
              </div>
              <div className="flex flex-1 flex-col gap-8 p-0">
                {bootcamp.cohorts.length ? (
                  <div className="w-full">
                    <div className="mb-2 text-sm uppercase tracking-wider">
                      Available Cohorts
                    </div>
                    <BootcampCohortList
                      cohorts={bootcamp.cohorts}
                      renderCohort={(cohort) => (
                        <div
                          key={cohort.id}
                          className="flex items-center justify-between rounded-lg bg-leland-white p-2 px-4 py-3"
                        >
                          <span className="text-lg font-medium text-leland-black">
                            {formatBootcampCohortDate(cohort)}
                          </span>
                          <Button
                            id="bootcamp_page--enroll_cohort"
                            buttonColor={ButtonColor.PRIMARY}
                            label="Enroll"
                            onClick={() =>
                              openBootcampCohortCheckoutPage(cohort.id)
                            }
                          />
                        </div>
                      )}
                    />
                  </div>
                ) : null}
                <BootcampGetNotifiedInput bootcampUrn={bootcamp.id} />
                {bootcamp.scholarshipUrl ? (
                  <div>
                    <Link
                      id="bootcamp_page--apply_for_scholarship_link"
                      href={bootcamp.scholarshipUrl}
                      className="text-lg underline"
                    >
                      Apply for a Scholarship
                    </Link>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        ) : null}
      </div>
      <SelectCohortModal
        cohorts={bootcamp.upcomingCohorts}
        maximumAttendees={bootcamp.maximumAttendees}
      />
      <BootcampBottomEnrollCTA
        bootcampTitle={bootcamp.title ?? headerText}
        numCohortsAvailable={bootcamp.cohorts.length}
        isVisible={
          isSelectCohortButtonVisible
            ? bootcamp.upcomingCohorts.length > 0
            : false
        }
        onEnrollClick={() => setIsSelectCohortModalOpen(true)}
      />
    </div>
  );
};

interface BootcampPageCurriculumSectionProps {
  bootcamp: BootcampPageBootcampFragment;
}

const BootcampPageCurriculumSection: FC<BootcampPageCurriculumSectionProps> = ({
  bootcamp,
}) => {
  const [selectedCohort] = useBootcampCohortSelector();

  interface BootcampCurriculumWithSession
    extends BootcampPageBootcampCurriculumFragment {
    startAt?: Date;
  }
  const curriculums: BootcampCurriculumWithSession[] =
    selectedCohort?.sessions.reduce<BootcampCurriculumWithSession[]>(
      (prev, curr) => {
        if (curr.content.__typename === 'BootcampSession') {
          return [
            ...prev,
            {
              ...curr.content.curriculum,
              startAt: curr.startAt ? new Date(curr.startAt) : undefined,
            },
          ];
        }
        return prev;
      },
      [],
    ) ?? bootcamp.curriculums.map((curriculum) => ({ ...curriculum }));

  return (
    <div className="bg-leland-dark-green bg-[url('/images/thin-white-plus.png')] bg-[length:50px_50px] bg-repeat-space bg-blend-soft-light">
      <div className="mx-auto w-full px-5 py-20 md:max-w-6xl md:px-10 md:py-24 xl:px-0">
        <div className="flex justify-between">
          <h2
            id="bootcamp_page--curriculum_header"
            className="mb-10 text-2xl font-medium text-leland-white md:text-4xl"
          >
            {bootcamp.htmlHeading.curriculums}
          </h2>
          {bootcamp.upcomingCohorts.length > 0 ? (
            <div className="hidden md:block">
              <BootcampCohortSelector />
            </div>
          ) : null}
        </div>
        <div className="flex flex-col gap-3 p-0">
          {curriculums.map((curriculum, index) => (
            <div
              key={index}
              className="flex gap-6 rounded-2xl bg-leland-white p-4 md:p-6"
            >
              {curriculum.startAt ? (
                <div className="flex flex-col text-center">
                  <div className="rounded-t-lg bg-leland-gray-dark p-3 px-5 py-0.5 text-lg font-medium text-leland-white">
                    {formatDate(curriculum.startAt, DateFormat.SHORT_MONTH)}
                  </div>
                  <div className="rounded-b-lg border-x-2 border-b-2 border-leland-gray-stroke p-3 px-4 py-0.5 text-lg font-medium">
                    {formatDate(curriculum.startAt, DateFormat.SHORT_DAY)}
                  </div>
                </div>
              ) : null}
              <div className="flex flex-col p-0">
                <h3 className="text-xl font-medium">{curriculum.title}</h3>
                <span className="text-lg text-leland-gray-light">
                  {curriculum.description}
                </span>
                {curriculum.startAt ? (
                  <span className="hidden text-lg text-leland-gray-extra-light md:block">
                    {formatDate(curriculum.startAt, DateFormat.LONG_STANDARD)}
                  </span>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

interface BootcampBottomEnrollCTAProps {
  bootcampTitle: string;
  numCohortsAvailable: number;
  isVisible: boolean;
  onEnrollClick: () => void;
}

const BootcampBottomEnrollCTA: React.FC<BootcampBottomEnrollCTAProps> = ({
  bootcampTitle,
  numCohortsAvailable,
  isVisible,
  onEnrollClick,
}) => {
  return (
    <div
      className={`fixed bottom-0 left-0 z-50 w-full border-t border-leland-gray-stroke bg-white px-4 py-3 transition-transform duration-300 ease-in-out md:hidden
         ${isVisible ? 'translate-y-0' : 'translate-y-full'}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex flex-col">
          <div className="text-lg font-medium text-leland-gray-dark">
            {bootcampTitle}
          </div>
          <div className="text-sm font-medium text-leland-gray-light">
            {pluralize(numCohortsAvailable, 'cohort').toUpperCase()} AVAILABLE
          </div>
        </div>
        <Button
          label="Enroll"
          buttonColor={ButtonColor.PRIMARY}
          onClick={onEnrollClick}
          size={ButtonSize.MEDIUM}
        />
      </div>
    </div>
  );
};

const BootcampPageWithProvider: FC<BootcampPageProps> = (props) => {
  return (
    <BootcampCohortSelectorProvider cohorts={props.bootcamp.upcomingCohorts}>
      <BootcampPage {...props} />
    </BootcampCohortSelectorProvider>
  );
};

export default BootcampPageWithProvider;
